import React, { useState } from "react";

import moment from "moment-timezone";
import sortBy from "lodash/sortBy";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";

import Avatar from "ui/Avatar";
import Form from "ui/Form";
import FormSubmit from "ui/FormSubmit";
import Grid from "ui/Grid";
import Loading from "ui/Loading";
import ModalContainer from "ui/ModalContainer";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import RevealableCopyIcon from "ui/RevealableCopyIcon";
import { formatDateShort } from "utils/format";
import { useRoutes } from "utils/routes";

import { ApiKeysQuery, useApiKeysQuery } from "./gql/Query.generated";

type ApiKeyType = Omit<ApiKeysQuery["getApiKeys"][number], "__typename">;

function ApiKeysContent({ organizationSlug }: { organizationSlug: string }) {
  const { data, loading, error } = useApiKeysQuery({
    variables: { organizationSlug },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const { slug, permittedToCreateApiKeys, permittedToCreateRWApiKeys } =
    data.getOrganizationDetails;

  if (!permittedToCreateApiKeys) {
    return (
      <Panel title="API Keys">
        <PanelSection>
          You are not allowed to view or create API keys - you do not have the{" "}
          <strong>API</strong> permission on this organization.
        </PanelSection>
      </Panel>
    );
  }

  const apiKeys = sortBy(
    data.getApiKeys,
    (k: ApiKeyType): number | null | undefined => k.lastUsedAt,
  );

  return (
    <Panel title="API Keys">
      <Grid
        data={apiKeys}
        defaultSortBy="createdAt"
        columns={[
          {
            field: "token",
            header: "Key",
            width: "320px",
            renderer: function TokenCell({ fieldData }) {
              return <Token token={fieldData} />;
            },
          },
          {
            field: "name",
            header: "Name",
            width: "1fr",
            nullValue: "-",
          },
          {
            field: "accessScopeType",
            header: "Scope",
            width: "1fr",
            renderer: function ScopeCell({ rowData }) {
              return (
                <>
                  {rowData.accessScopeType}: {rowData.accessScopeName}
                </>
              );
            },
          },
          { field: "accessType", header: "Access Type", width: "120px" },
          {
            field: "lastUsedAt",
            header: "Last Used At",
            width: "120px",
            nullValue: "-",
            renderer: function LastUsedAtCell({ fieldData }) {
              return formatDateShort(moment.unix(fieldData));
            },
          },
          {
            field: "createdAt",
            header: "Created At",
            width: "125px",
            renderer: function CreatedAtCell({ fieldData, rowData }) {
              return (
                <span className="flex items-center">
                  {formatDateShort(moment.unix(fieldData))}
                  {rowData.creator && (
                    <span
                      className="ml-2 leading-[0]"
                      title={`Created by ${
                        rowData.creator.fullname ?? rowData.creator.email
                      }`}
                    >
                      <Avatar
                        avatarUrl={rowData.creator.avatarUrl}
                        name={rowData.creator.fullname ?? rowData.creator.email}
                        hideName
                      />
                    </span>
                  )}
                </span>
              );
            },
          },
          {
            field: "id",
            header: "",
            width: "40px",
            renderer: function RevokeCell({ rowData }) {
              return (
                <a
                  href={`/organizations/${organizationSlug}/api/${rowData.id}`}
                  data-method="delete"
                  data-confirm={`Revoke the API key ${
                    rowData.name ? `"${rowData.name}" ` : ""
                  }of ${rowData.accessScopeType.toLowerCase()} ${
                    rowData.accessScopeName
                  } (${rowData.accessType})?`}
                  title="Revoke"
                >
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className="text-[#CA1515] cursor-pointer"
                  />
                </a>
              );
            },
          },
        ]}
      />
      <PanelSection>
        <NewApiKeyButton
          organizationSlug={slug}
          permittedToCreateRWApiKeys={permittedToCreateRWApiKeys}
        />
      </PanelSection>
    </Panel>
  );
}

function NewApiKeyButton({
  organizationSlug,
  permittedToCreateRWApiKeys,
}: {
  organizationSlug: string;
  permittedToCreateRWApiKeys: boolean;
}) {
  const { organizationApi } = useRoutes();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="btn btn-success"
        onClick={() => setOpen(true)}
      >
        Create API Key
      </button>
      {open && (
        <ModalContainer
          title="Create New API Key"
          layout="centered"
          className="!min-w-0"
          onClose={() => setOpen(false)}
        >
          <Form
            action={organizationApi(organizationSlug)}
            insideSection
            className="lg:min-w-[560px]"
          >
            <p>
              Choose the access type of the new API key (the key will be created
              at the organization level):
            </p>
            <div className="form-group">
              <input
                type="radio"
                name="scope"
                value="read"
                id="read_scope"
                defaultChecked
              />{" "}
              <label className="text-[#555]" htmlFor="read_scope">
                Read API Key
              </label>
              <div className="help-block">
                Can be used to retrieve statistics data and pganalyze settings
                with the pganalyze GraphQL API
              </div>
              <input
                type="radio"
                name="scope"
                value="collector"
                id="collector_scope"
              />{" "}
              <label className="text-[#555]" htmlFor="collector_scope">
                Collector API Key
              </label>
              <div className="help-block">
                Can be used by the collector to send database statistics
                snapshots to pganalyze
              </div>
              {permittedToCreateRWApiKeys && (
                <>
                  <input
                    type="radio"
                    name="scope"
                    value="read_write"
                    id="read_write_scope"
                  />{" "}
                  <label className="text-[#555]" htmlFor="read_write_scope">
                    Read-Write API Key
                  </label>
                  <div className="help-block">
                    Can be used to retrieve statistics data and pganalyze
                    settings, and change pganalyze settings with the pganalyze
                    GraphQL API
                  </div>
                </>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="api_key_name">
                Name <span className="font-normal text-[#666]">(optional)</span>
              </label>
              <input
                type="text"
                name="name"
                id="api_key_name"
                className="form-control"
                maxLength={200}
                placeholder="e.g. Production collector"
              />
            </div>
            <FormSubmit title="Create new API Key" />
          </Form>
        </ModalContainer>
      )}
    </>
  );
}

function Token({ token }: { token: string }) {
  const [show, setShow] = useState(false);

  const handleShow = () => {
    setShow(true);
  };

  let tokenText;
  if (show) {
    tokenText = token;
  } else {
    // mask with * except beginning of token
    const unmaskedLen = /^pga[cwr]_/.test(token)
      ? 8 // for new-style tokens, we have a fixed prefix, so we can show a bit past that
      : 3; // otherwise just show the first three characters

    if (token.length >= unmaskedLen) {
      tokenText =
        token.substring(0, unmaskedLen) +
        "*".repeat(token.length - unmaskedLen);
    } else {
      tokenText = "*".repeat(token.length);
    }
  }

  return (
    <div className="flex justify-between">
      <code className="bg-[#f7fafc] text-[#333]">{tokenText}</code>{" "}
      <RevealableCopyIcon
        content={token}
        hidden={!show}
        onReveal={handleShow}
      />
    </div>
  );
}

export default ApiKeysContent;
